/**
 * WorldPulseCommunity — 10-category community panel for a country.
 *
 * Reads `/api/wp/community/{iso}` (Wikipedia-seeded categories + counts)
 * plus `/api/wp/community/{iso}/category/{cat}` on tab switch (lazy).
 * Falls back gracefully when a category is empty so newer countries
 * don't show a dead panel.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import {
  Landmark, Palette, TreePine, Briefcase, Users, CalendarDays,
  MapPin, Newspaper, UtensilsCrossed, Flame, ExternalLink, Plus,
} from "lucide-react";
import ContributionModal from "@/components/ContributionModal";

const CATEGORY_ICONS = {
  history:   Landmark,
  culture:   Palette,
  nature:    TreePine,
  business:  Briefcase,
  people:    Users,
  events:    CalendarDays,
  landmarks: MapPin,
  news:      Newspaper,
  food:      UtensilsCrossed,
  memorials: Flame,
};

const CATEGORY_LABELS = {
  history:   "History",
  culture:   "Culture",
  nature:    "Nature",
  business:  "Business",
  people:    "People",
  events:    "Events",
  landmarks: "Landmarks",
  news:      "News",
  food:      "Food",
  memorials: "Memorials",
};

export default function WorldPulseCommunity({ iso, locationId, accent = "#4f46e5" }) {
  const [summary, setSummary] = useState(null);
  const [active, setActive]   = useState("history");
  const [tabData, setTabData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contribOpen, setContribOpen] = useState(false);
  const [refreshKey, setRefreshKey]   = useState(0);

  useEffect(() => {
    if (!iso && !locationId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // Country-level (has ISO code) uses the /wp/community/{iso} unified summary.
        // Any other location uses /wp/community/place/{location_id}.
        const url = iso
          ? `${API}/wp/community/${iso}`
          : `${API}/wp/community/place/${locationId}`;
        const r = await axios.get(url);
        if (!cancelled) setSummary(r.data);
      } catch { if (!cancelled) setSummary(null); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [iso, locationId, refreshKey]);

  useEffect(() => {
    // Category-specific lazy load only for country pages (has ISO).
    // Place pages already ship their categories in the summary.
    if (!iso || !active) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/wp/community/${iso}/category/${active}`);
        if (!cancelled) setTabData(r.data);
      } catch { if (!cancelled) setTabData(null); }
    })();
    return () => { cancelled = true; };
  }, [iso, active, refreshKey]);

  if (loading) return null;
  if (!summary) return null;

  // Country summary has `country + counts`. Place summary has `location + total_items`.
  const isCountry = !!summary.country;
  const displayName = isCountry
    ? (summary.country?.official_name || iso)
    : (summary.location?.official_name || "this place");

  const activeItems =
    active === "memorials" || active === "news"
      ? (tabData?.items || [])
      : (tabData?.items || summary.categories.find(c => c.category === active)?.items || []);

  return (
    <section
      className="bg-white border rounded-2xl p-5 sm:p-6"
      data-testid="wp-community"
    >
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-3">
        <div>
          <div
            className="text-xs uppercase tracking-[0.2em] mb-1"
            style={{ color: accent }}
          >
            Community
          </div>
          <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight">
            Explore {displayName}
          </h2>
        </div>
        <div className="flex items-center gap-3">
          {isCountry ? (
            <div className="flex gap-4 text-xs text-slate-500">
              <span data-testid="wp-com-count-orgs">{summary.counts?.orgs || 0} orgs</span>
              <span data-testid="wp-com-count-mem">{summary.counts?.memorials || 0} memorials</span>
              <span data-testid="wp-com-count-photos">{summary.counts?.photos || 0} photos</span>
              <span data-testid="wp-com-count-people">{summary.counts?.people || 0} residents</span>
            </div>
          ) : (
            summary.needs_seed && (
              <div className="text-xs text-slate-500" data-testid="wp-com-seeding">
                Fresh content generating — refresh in ~30s
              </div>
            )
          )}
          {/* Contribute button — signed-in users only. Guest click prompts login. */}
          {locationId && (
            <button
              onClick={() => setContribOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-white shadow-sm hover:shadow"
              style={{ background: accent }}
              data-testid="wp-community-contribute-btn"
            >
              <Plus className="w-3.5 h-3.5" /> Contribute
            </button>
          )}
        </div>
      </div>

      {/* Category tab strip */}
      <div
        className="flex flex-wrap gap-1.5 mb-5 border-b border-slate-100 pb-3"
        data-testid="wp-community-tabs"
      >
        {summary.categories.map((c) => {
          const Icon = CATEGORY_ICONS[c.category];
          const on   = c.category === active;
          return (
            <button
              key={c.category}
              onClick={() => setActive(c.category)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition ${
                on
                  ? "text-white shadow"
                  : "text-slate-600 hover:bg-slate-100"
              }`}
              style={on ? { background: accent } : undefined}
              data-testid={`wp-community-tab-${c.category}`}
            >
              <Icon className="w-3.5 h-3.5" />
              {CATEGORY_LABELS[c.category]}
            </button>
          );
        })}
      </div>

      {/* Active category content */}
      {active === "memorials" ? (
        <MemorialsGrid items={activeItems} accent={accent} />
      ) : active === "news" ? (
        <NewsGrid items={activeItems} />
      ) : (
        <SeedContentGrid items={activeItems} category={active} accent={accent} />
      )}

      <ContributionModal
        open={contribOpen}
        onClose={() => setContribOpen(false)}
        locationId={locationId}
        locationName={displayName}
        defaultCategory={active === "news" || active === "memorials" ? "history" : active}
        accent={accent}
        onSubmitted={() => setRefreshKey(k => k + 1)}
      />
    </section>
  );
}


function SeedContentGrid({ items, category, accent }) {
  if (!items.length) {
    return (
      <div
        className="text-center py-10 text-sm text-slate-500 bg-slate-50 rounded-xl"
        data-testid="wp-community-empty"
      >
        <div className="mb-2">No {CATEGORY_LABELS[category].toLowerCase()} content yet.</div>
        <button
          className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full text-white"
          style={{ background: accent }}
          data-testid="wp-community-contribute"
        >
          Be the first to contribute
        </button>
      </div>
    );
  }
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="wp-community-grid">
      {items.map((it) => {
        const Wrapper = it.wiki_url ? "a" : "div";
        const wrapperProps = it.wiki_url
          ? { href: it.wiki_url, target: "_blank", rel: "noreferrer" }
          : {};
        return (
          <Wrapper
            key={it.id}
            {...wrapperProps}
            className={`group rounded-xl overflow-hidden border border-slate-200 bg-white transition ${
              it.wiki_url ? "hover:shadow-lg" : ""
            }`}
            data-testid={`wp-community-item-${it.id}`}
          >
            {it.image_url && (
              <div
                className="h-40 bg-slate-100 bg-cover bg-center"
                style={{ backgroundImage: `url(${it.image_url})` }}
              />
            )}
            <div className="p-4">
              <div className={`text-sm font-semibold text-slate-900 line-clamp-2 ${
                it.wiki_url ? "group-hover:underline" : ""
              }`}>
                {it.title}
              </div>
              {it.description && (
                <div className="text-xs text-slate-500 mt-0.5 line-clamp-1">
                  {it.description}
                </div>
              )}
              {it.summary && (
                <p className="text-xs text-slate-600 mt-2 line-clamp-3 leading-relaxed">
                  {it.summary}
                </p>
              )}
              <div className="text-[10px] text-slate-400 mt-3 flex items-center gap-1">
                {it.source === "wikipedia" ? (
                  <>via Wikipedia <ExternalLink className="w-2.5 h-2.5" /></>
                ) : it.source?.startsWith("ai:") ? (
                  <>AI-generated summary</>
                ) : (
                  <>via {it.source || "community"}</>
                )}
              </div>
            </div>
          </Wrapper>
        );
      })}
    </div>
  );
}


function MemorialsGrid({ items, accent }) {
  if (!items.length) {
    return (
      <div className="text-center py-10 text-sm text-slate-500" data-testid="wp-community-mem-empty">
        No memorials on record yet.
      </div>
    );
  }
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="wp-community-memorials">
      {items.map((m) => (
        <a
          key={m.id}
          href={`/memorial/${m.slug || m.id}`}
          className="group rounded-xl overflow-hidden border border-slate-200 bg-white hover:shadow-lg transition"
          data-testid={`wp-memorial-${m.id}`}
        >
          {m.portrait && (
            <div
              className="h-32 bg-slate-100 bg-cover bg-center"
              style={{ backgroundImage: `url(${m.portrait})` }}
            />
          )}
          <div className="p-3">
            <div className="text-sm font-semibold text-slate-900 group-hover:underline truncate">
              {m.full_name}
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              {[m.birth_date, m.death_date].filter(Boolean).join(" – ")}
            </div>
            {m.short_bio && (
              <p className="text-xs text-slate-600 mt-1 line-clamp-2">{m.short_bio}</p>
            )}
          </div>
        </a>
      ))}
    </div>
  );
}


function NewsGrid({ items }) {
  if (!items.length) {
    return (
      <div className="text-center py-10 text-sm text-slate-500" data-testid="wp-community-news-empty">
        No news feeds active for this country yet.
      </div>
    );
  }
  return (
    <div className="grid sm:grid-cols-2 gap-3" data-testid="wp-community-news">
      {items.map((n, i) => (
        <a
          key={n.guid || i}
          href={n.original_url}
          target="_blank"
          rel="noreferrer"
          className="flex gap-3 rounded-xl border border-slate-200 bg-white p-3 hover:shadow transition"
          data-testid={`wp-com-news-${i}`}
        >
          {n.image_url && (
            <img
              src={n.image_url}
              alt=""
              className="w-16 h-16 rounded object-cover flex-shrink-0"
              loading="lazy"
            />
          )}
          <div className="min-w-0">
            <div className="text-sm font-medium text-slate-900 leading-snug line-clamp-2">
              {n.headline}
            </div>
            <div className="text-[11px] text-slate-500 mt-1 truncate">{n.source_name}</div>
          </div>
        </a>
      ))}
    </div>
  );
}
